/**
 * Presets (rules v0.6 §13.6 turn scripts, §17 difficulty dial) and pacing.
 */
import type { LayerKey, Pacing, PacingKey, PresetKey, Rate, Role } from '../types.ts';

export interface ScriptRow {
  arrivals: number;
  /** Event keys; 'W' = wildcard draw (pre-drawn at Start shift). */
  events: string[];
}

export interface PresetDials {
  key: PresetKey;
  label: string;
  blurb: string;
  calibration: Record<50 | 70 | 90, number>;
  bounceBackBacks: number;
  labelledCards: number;
  labelTraps: number;
  inconclusiveShare: number;
  complShare: Record<Rate, number>;
  early: { bounce1: number; bounce2: number };
  barnacles: number;
  divert: boolean;
  callsForHelp: number;
  constraintRoles: number;
  regionalObjectives: boolean;
  icuBeds: number;
  script: ScriptRow[]; // index 0 = script turn 1 … index 9 = script turn 10 (handover)
  wildcards: string[];
  layers: Record<LayerKey, boolean>;
  staff: Record<Role, number>;
  vents: number;
  blood: number;
}

const HONEST = { 90: 0.9, 70: 0.7, 50: 0.5 } as const;
const OVERCONFIDENT = { 90: 0.8, 70: 0.55, 50: 0.4 } as const;

export const PRESETS: Record<PresetKey, PresetDials> = {
  ORIENTATION: {
    key: 'ORIENTATION',
    label: 'Orientation',
    blurb: 'First play. Honest numbers, no diversion.',
    calibration: HONEST,
    bounceBackBacks: 2,
    labelledCards: 10,
    labelTraps: 5,
    inconclusiveShare: 0,
    complShare: { LOW: 0.05, MED: 0.1, HIGH: 0.15 },
    early: { bounce1: 0.1, bounce2: 0.1 },
    barnacles: 0,
    divert: false,
    callsForHelp: 2,
    constraintRoles: 0,
    regionalObjectives: false,
    icuBeds: 2,
    script: [
      { arrivals: 3, events: [] },
      { arrivals: 3, events: ['E28'] },
      { arrivals: 2, events: ['E1'] },
      { arrivals: 1, events: ['E14'] },
      { arrivals: 2, events: ['E18'] },
      { arrivals: 3, events: ['E12', 'E27'] },
      { arrivals: 2, events: ['E23'] },
      { arrivals: 1, events: ['W'] },
      { arrivals: 2, events: ['E17'] },
      { arrivals: 0, events: [] },
    ],
    wildcards: ['E30', 'E31', 'E32', 'E33', 'E44'],
    layers: { staff: false, vents: false, blood: false },
    // Layer numbers set by the load study (D-45): Staff 1.75× the first guess, blood halved, one fewer vent here.
    staff: { EM: 9, SURG: 7, PHYS: 9, PSYCH: 4, ICU: 7 },
    vents: 2,
    blood: 4,
  },
  MIDYEAR: {
    key: 'MIDYEAR',
    label: 'Mid-year',
    blurb: 'Overconfident numbers, diversion between tables.',
    calibration: OVERCONFIDENT,
    bounceBackBacks: 4,
    labelledCards: 10,
    labelTraps: 6,
    inconclusiveShare: 0.1,
    complShare: { LOW: 0.05, MED: 0.15, HIGH: 0.25 },
    early: { bounce1: 0.15, bounce2: 0.1 },
    barnacles: 0,
    divert: true,
    callsForHelp: 2,
    constraintRoles: 0,
    regionalObjectives: true,
    icuBeds: 2,
    script: [
      { arrivals: 3, events: ['E5'] },
      { arrivals: 2, events: ['E6'] },
      { arrivals: 2, events: ['E19'] },
      { arrivals: 2, events: ['E17'] },
      { arrivals: 2, events: ['E26'] },
      { arrivals: 2, events: ['E24', 'E29'] },
      { arrivals: 2, events: ['E25'] },
      { arrivals: 2, events: ['W'] },
      { arrivals: 1, events: ['W', 'E21'] },
      { arrivals: 0, events: [] },
    ],
    wildcards: ['E1', 'E2', 'E3', 'E4', 'E8', 'E10', 'E14', 'E15', 'E18', 'E22', 'E30', 'E34', 'E42', 'E43', 'E44', 'E45'],
    layers: { staff: false, vents: false, blood: false },
    staff: { EM: 9, SURG: 5, PHYS: 7, PSYCH: 4, ICU: 7 },
    vents: 2,
    blood: 3,
  },
  EXIT: {
    key: 'EXIT',
    label: 'Exit',
    blurb: 'Overconfident, "not on the list" cards, constraint roles, one call for help.',
    calibration: OVERCONFIDENT,
    bounceBackBacks: 6,
    labelledCards: 10,
    labelTraps: 8,
    inconclusiveShare: 0.2,
    complShare: { LOW: 0.1, MED: 0.2, HIGH: 0.3 },
    early: { bounce1: 0.2, bounce2: 0.1 },
    barnacles: 4,
    divert: true,
    callsForHelp: 1,
    constraintRoles: 3,
    regionalObjectives: true,
    icuBeds: 2,
    script: [
      { arrivals: 3, events: ['E8', 'E5'] },
      { arrivals: 3, events: ['E17'] },
      { arrivals: 2, events: ['E6'] },
      { arrivals: 2, events: ['E23'] },
      { arrivals: 1, events: ['E13'] },
      { arrivals: 2, events: ['E26'] },
      { arrivals: 3, events: ['E12'] },
      { arrivals: 2, events: ['E25', 'E20'] },
      { arrivals: 2, events: ['E24', 'E21'] },
      { arrivals: 1, events: [] },
    ],
    wildcards: ['E1', 'E2', 'E3', 'E4', 'E8', 'E10', 'E14', 'E15', 'E18', 'E22', 'E30', 'E34', 'E35', 'E42', 'E43', 'E44', 'E45'],
    layers: { staff: false, vents: false, blood: false },
    staff: { EM: 7, SURG: 5, PHYS: 7, PSYCH: 2, ICU: 7 },
    vents: 2,
    blood: 3,
  },
};

/** Layer events added to the wildcard pile when the layer is on. */
export const LAYER_WILDCARDS: Partial<Record<LayerKey, string[]>> = {
  staff: ['E36'],
  vents: ['E39'],
  blood: ['E41'],
};

export const PACINGS: Record<Exclude<PacingKey, 'CUSTOM'>, Pacing> = {
  STANDARD: { key: 'STANDARD', scriptTurns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], arrivalsMs: 20000, huddleMs: 90000, actMs: 150000, tickMs: 15000, logMs: 10000, handoverMs: 120000 },
  NINETY: { key: 'NINETY', scriptTurns: [1, 2, 3, 4, 6, 7, 10], arrivalsMs: 20000, huddleMs: 90000, actMs: 150000, tickMs: 15000, logMs: 10000, handoverMs: 120000 },
  TEACHING: { key: 'TEACHING', scriptTurns: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], arrivalsMs: 25000, huddleMs: 120000, actMs: 180000, tickMs: 20000, logMs: 10000, handoverMs: 150000 },
};

/** Custom pacing: `turns` game turns spread over script turns 1..10, always including 1 and 10. */
export function customPacing(turns: number, huddleS: number, actS: number): Pacing {
  const n = Math.max(3, Math.min(12, Math.round(turns)));
  const scriptTurns: number[] = [];
  if (n >= 10) {
    for (let i = 1; i <= 10; i++) scriptTurns.push(i);
    // more than ten turns: repeat quiet middle turns (script turn 8) before the handover
    while (scriptTurns.length < n) scriptTurns.splice(scriptTurns.length - 1, 0, 8);
  } else {
    for (let i = 0; i < n; i++) scriptTurns.push(Math.round(1 + (i * 9) / (n - 1)));
  }
  return {
    key: 'CUSTOM',
    scriptTurns,
    arrivalsMs: 20000,
    huddleMs: Math.max(30, Math.min(300, huddleS)) * 1000,
    actMs: Math.max(60, Math.min(300, actS)) * 1000,
    tickMs: 15000,
    logMs: 10000,
    handoverMs: 120000,
  };
}

/** In-fiction hour for a script turn: turn 1 = 2100, turn 10 = 0600, handover = 0700. */
export function shiftHour(scriptTurn: number, handover: boolean): string {
  const h = (21 + scriptTurn - 1 + (handover ? 1 : 0)) % 24;
  return `${String(h).padStart(2, '0')}00`;
}
